import StatusScreen from "@/components/status-screen";
import StyledTooltip from "@/components/styled-tooltip";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import InboxPlacementReport from "@/pages/inbox-placement/inbox-placement-report";
import { PlacementProvider } from "@/pages/inbox-placement/inbox-placement-report/placement-provider";
import PlacementHistoryTable from "@/pages/inbox-placement/placement-history-table";
import { PlacementType } from "@/types/inboxPlacements.types";
import {
  createFileRoute,
  Link,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { Info, X } from "lucide-react";
import { z } from "zod";

const searchParamsSchema = z.object({
  type: z.nativeEnum(PlacementType, {
    required_error: "Type is required",
    invalid_type_error: "Type is invalid",
  }),

  _testId: z
    .string({ required_error: "Test ID is required" })
    .min(1, "Test ID is required"),
});

export const Route = createFileRoute(
  "/_private/placement-test/$placement-test"
)({
  component: RouteComponent,
  validateSearch: (search) => searchParamsSchema.parse(search),
  errorComponent: () => {
    return (
      <StatusScreen
        title="Invalid URL"
        description="The report URL is invalid. Type (B2B or B2C) and test ID are required."
        variant="actions"
        actions={
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Link to=".">
              <Button variant="outline">Go back</Button>
            </Link>
            <Link to="/placement-test" search={{ type: PlacementType.B2B }}>
              <Button>Run new test</Button>
            </Link>
          </div>
        }
        icon={<X size={40} />}
        iconWrapperClassName="text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
        wrapperClass="min-h-[calc(100vh-150px)] flex justify-center items-center w-full"
      />
    );
  },
});

function RouteComponent() {
  const { "placement-test": placementTest } = useParams({
    from: "/_private/placement-test/$placement-test",
  });

  const { type, _testId } = useSearch({
    from: "/_private/placement-test/$placement-test",
  });

  return (
    <div className="w-full">
      <PlacementProvider email={placementTest} type={type} testId={_testId}>
        <InboxPlacementReport />
      </PlacementProvider>
      <div className="space-y-2 mt-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium">Inbox Placement History</h2>
          <StyledTooltip description="Shows the history of your emails being delivered to different providers.">
            <Info className="h-4 w-4 text-muted-foreground" />
          </StyledTooltip>
        </div>

        <Card>
          <CardContent className="p-4">
            <PlacementHistoryTable
              reportId={_testId}
              email={placementTest}
              type={type}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
